/**
 * 已选房间模块
 */
import React, { useMemo, useRef, memo, useEffect } from 'react';
import { connect } from 'react-redux';
import { Table, Button } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import intl from 'react-intl-universal';

import InputWithCoupon from '@/components/Reservation/NumberInput/InputWithCoupon';
import CouponSelector, { CouponSelectorRef } from '@/components/Reservation/CouponSelector';
import CouponGapText from '@/components/Reservation/CouponGapText';
import { MemberType, RESERVATION_SCENE } from '@/config/constant';
import { parseYuan2Fen, Calculator, parseFen2Yuan, format2Decimal } from '@/utils/number';
import {
  createStateSelector,
  findItemByKey,
  getEmptyCouponData,
} from '@/redux/actions/reservation/helper';
import { handleRentItemChange, handleRentItemDelete } from '@/redux/actions/reservation/checkIn';
import { CouponValue, RentRoomInfo, RoomPriceInfo } from '@/redux/reducers/reservation/base.d';
import { ReducerState, storeName } from '@/redux/reducers/reservation/checkIn';
import CouponService from '@/components/Reservation/CouponService';
import useUpdate from '@/hooks/useUpdate';
import { getOccSummaryText } from '@/pages/reservation/utils/formatter';

import GuestSelector from './GuestSelector';

import styles from './index.module.scss';

const stateSelector = (state: ReducerState) => {
  const { rentRoomInfos, allGuestList, commonInfo, channelList, channelInfo } = state;
  return {
    rentRoomInfos,
    allGuestList,
    occType: commonInfo.occType,
    channelList,
    sourceChannelId: channelInfo.channelId,
    isAppendMode: !!commonInfo.appendOrderId,
  };
};
const mapStateToProps = createStateSelector<ReducerState>(storeName, stateSelector);
const mapDispatchToProps = {
  callRentItemDelete: handleRentItemDelete,
  callRentItemChange: handleRentItemChange,
};

type MapStateProps = ReturnType<typeof stateSelector>;
type MapDispatchProps = typeof mapDispatchToProps;

const Index: React.FC<MapStateProps & MapDispatchProps> = (props) => {
  const {
    callRentItemChange,
    callRentItemDelete,
    rentRoomInfos,
    allGuestList,
    occType,
    channelList,
    sourceChannelId,
    isAppendMode,
  } = props;

  const couponSelectorRef = useRef<CouponSelectorRef>({
    openModal: () => {},
    closeModal: () => {},
  });

  const masterId = useMemo(() => {
    // 房主
    const ownerInfo = findItemByKey(allGuestList, 'memberType', MemberType.HouseOwner);
    return ownerInfo ? ownerInfo.wyuId : null;
  }, [allGuestList]);

  // 当前渠道类型
  const currentChannelType = useMemo(() => {
    const channelItem = findItemByKey(channelList, 'id', sourceChannelId);
    return channelItem?.type;
  }, [channelList, sourceChannelId]);

  const forceUpdate = useUpdate();

  const couponService = useMemo(() => {
    return new CouponService();
  }, []);

  useEffect(() => {
    // 获取当前用户可用券
    couponService.initCouponList(masterId).then(forceUpdate);
  }, [masterId]);

  // 更新券状态
  const updateCouponService = () => {
    try {
      // 恢复券状态
      couponService.resetWYListStatus();
      const lockList = [];
      rentRoomInfos.forEach((roomItem) => {
        (roomItem.roomPriceList || []).forEach((rentDay) => {
          if (rentDay.couponNo) {
            lockList.push(rentDay.couponNo);
          }
        });
      });
      // 批量锁定券
      couponService.batchLockWYCoupon(lockList);
    } catch (e) {
      //
    }
  };
  updateCouponService();

  // 押金
  const handleDepositChange: (deposit: string, record: RentRoomInfo) => void = (
    deposit,
    record,
  ) => {
    // if (PRICE_REG.test(deposit)) {
    callRentItemChange(record.indexId, { deposit });
    // }
  };

  // 入住房价
  const handlePriceChange: (text: string, dayPrice: RoomPriceInfo, record: RentRoomInfo) => void = (
    text,
    dayPrice,
    record,
  ) => {
    // if (PRICE_REG.test(text)) {
    const { rentDateId } = dayPrice;
    callRentItemChange(record.indexId, {
      roomPriceList: (record.roomPriceList || []).map((item) => {
        if (item.rentDateId === rentDateId) {
          return {
            ...item,
            // 价格修改 删除已选券
            ...getEmptyCouponData(),
            inputPrice: text,
          };
        }
        return item;
      }),
    });
    // }
  };

  // 优惠券
  const showCouponSelector: (priceInfo: RoomPriceInfo, record: RentRoomInfo) => void = (
    priceInfo,
    record,
  ) => {
    const { inputPrice, couponNo, rentDateId } = priceInfo;
    const lockCouponList = []; // 所有已选的优惠券
    rentRoomInfos.forEach((roomInfo) => {
      (roomInfo.roomPriceList || []).forEach((dayInfo) => {
        const itemNo = dayInfo.couponNo;
        if (itemNo) {
          lockCouponList.push(itemNo);
        }
      });
    });
    couponSelectorRef.current.openModal({
      wyuid: masterId,
      occType,
      sceneType: isAppendMode ? RESERVATION_SCENE.ExtendCheckIn : RESERVATION_SCENE.NewCheckIn,
      amount: parseYuan2Fen(inputPrice || 0),
      changedCouponId: couponNo ? [couponNo] : [],
      lockCouponList,
      unlockCouponList: [],
      channelType: currentChannelType,
      businessDate: priceInfo.rentDate,
      callback: (_, infoList) => {
        // 更新手工券
        couponService.refreshManualList().then(forceUpdate);
        const [targetDetail] = infoList;
        let updateCouponInfo: CouponValue = getEmptyCouponData();
        if (targetDetail) {
          const { couponFaceValue, couponName, couponType, wyuid, couponSubType } = targetDetail;
          updateCouponInfo = {
            couponValue: parseFen2Yuan(couponFaceValue || 0),
            couponAmount: couponFaceValue,
            couponName,
            couponNo: targetDetail.couponNo,
            couponType,
            couponWyuid: wyuid,
            couponSubType,
          };
        }
        callRentItemChange(record.indexId, {
          roomPriceList: (record.roomPriceList || []).map((item) => {
            if (item.rentDateId === rentDateId) {
              return { ...item, ...updateCouponInfo };
            }
            return item;
          }),
        });
      },
    });
  };

  const renderDepositItem: (deposit: string, record: RentRoomInfo) => React.ReactNode = (
    deposit,
    record,
  ) => {
    return (
      <InputWithCoupon
        withCoupon={false}
        value={deposit}
        onChange={(val) => handleDepositChange(val, record)}
      />
    );
  };

  const renderGuestSelect: (recordId: string) => React.ReactNode = (recordId) => {
    return <GuestSelector recordId={recordId} />;
  };

  const renderPriceItem: (dayPrice: RoomPriceInfo, record: RentRoomInfo) => React.ReactNode = (
    dayPrice,
    record,
  ) => {
    // 券是否可用
    const isCouponDisabled = couponService.isCouponDisabled({
      priceChannel: currentChannelType,
      exclude: [dayPrice.couponNo],
    });
    return (
      <InputWithCoupon
        value={dayPrice.inputPrice}
        couponDisabled={isCouponDisabled}
        couponValue={dayPrice.couponValue}
        showCouponSelector={() => showCouponSelector(dayPrice, record)}
        onChange={(val) => handlePriceChange(val, dayPrice, record)}
      />
    );
  };

  const getTableInfo = () => {
    // 左侧固定列
    const fixedLeftColumns: ColumnsType<RentRoomInfo> = [
      {
        title: intl.get('app_constants_room_type'),
        dataIndex: 'roomTypeName',
        key: 'roomTypeName',
        fixed: 'left',
        width: 160,
      },
      {
        title: intl.get('app_constants_room_no'),
        dataIndex: 'roomNo',
        key: 'roomNo',
        fixed: 'left',
        width: 140,
      },
      {
        title: intl.get('app_constants_occupant_name'),
        dataIndex: 'indexId',
        key: 'guestList',
        width: 260,
        render: renderGuestSelect,
      },
      {
        title: intl.get('app_reservation_deposit_amount'),
        dataIndex: 'deposit',
        key: 'deposit',
        width: 180,
        align: 'right',
        render: renderDepositItem,
      },
    ];
    // 右侧操作列
    const fixedRightColumns: ColumnsType<RentRoomInfo> = [
      {
        title: intl.get('app_table_column_action'),
        key: 'operation',
        dataIndex: 'indexId',
        fixed: 'right',
        width: 60,
        align: 'right',
        render: (indexId) => (
          <Button
            type="text"
            className={styles.deleteBtn}
            onClick={() => callRentItemDelete(indexId)}
          >
            {intl.get('reservation_room_delete_btn')}
          </Button>
        ),
      },
    ];
    // 入住日期列
    const rentDayColumns: ColumnsType<RentRoomInfo> = [];
    const rentDaySummary = []; // 日期总计栏
    const totalPrice = new Calculator(0); // 总价格
    const totalDeposit = new Calculator(0); // 总押金
    let totalDay = 0; // 租住天数
    let cellIndex = fixedLeftColumns.length; // 总计栏下标=左侧固定列长度
    let guestCount = 0; // 已选宾客数量
    if (rentRoomInfos.length) {
      const { roomPriceList } = rentRoomInfos[0]; // 入住天价格列表
      const rentDays = roomPriceList || [];
      const guestIdMap = {};
      totalDay = rentDays.length;
      // 总押金计算
      rentRoomInfos.forEach((item) => {
        totalDeposit.plus(item.deposit || 0);
      });
      rentDays.forEach((rentDay, idx) => {
        const { rentDate } = rentDay;
        // 入住日期列
        rentDayColumns.push({
          title: <CouponGapText text={rentDate} />,
          dataIndex: ['roomPriceList', idx],
          width: 260,
          align: 'right',
          render: (value, record) => renderPriceItem(value, record),
        });
        const dayPrice = new Calculator(0); // 当天房间总价
        rentRoomInfos.forEach((room) => {
          const priceList = room.roomPriceList || []; // 房间房价列表
          const dayInfo = findItemByKey(priceList, 'rentDate', rentDate); // 当日房价信息
          dayPrice.plus(dayInfo?.inputPrice || 0).minus(dayInfo?.couponValue || 0);
        });
        totalPrice.plus(dayPrice.getValue()); // 总房价 +当天所有房间总价
        // 当日总计栏
        rentDaySummary.push(
          <Table.Summary.Cell index={cellIndex} key={cellIndex} align="right">
            <CouponGapText text={format2Decimal(dayPrice.getValue())} />
          </Table.Summary.Cell>,
        );
        cellIndex += 1; // 总计栏下标+1
      });
      rentRoomInfos.forEach((room) => {
        (room.guestList || []).forEach((guestId) => {
          // 宾客未入住其它房间 计数+1
          if (!guestIdMap[guestId]) {
            guestIdMap[guestId] = guestId;
            guestCount += 1;
          }
        });
      });
    }
    return {
      tableColumns: [...fixedLeftColumns, ...rentDayColumns, ...fixedRightColumns],
      tableSummary: [
        <Table.Summary.Cell index={0} key="0">
          {intl.get('reservation_room_summary_label')}
        </Table.Summary.Cell>,
        <Table.Summary.Cell index={1} key="1">
          {rentRoomInfos.length}
        </Table.Summary.Cell>,
        <Table.Summary.Cell index={2} key="2">
          {guestCount}
        </Table.Summary.Cell>,
        <Table.Summary.Cell index={3} key="3" align="right">
          {format2Decimal(totalDeposit.getValue())}
        </Table.Summary.Cell>,
        ...rentDaySummary,
        <Table.Summary.Cell index={cellIndex} key={cellIndex} />,
      ],
      tableFooter: getOccSummaryText({
        count: rentRoomInfos.length,
        type: occType,
        duration: totalDay,
        amount: totalPrice.getValue(),
      }),
    };
  };
  const tableInfo = getTableInfo();

  return (
    <>
      <Table
        scroll={{ x: 100, y: 560 }}
        pagination={false}
        className={styles.rentRoomContainer}
        rowKey="indexId"
        columns={tableInfo.tableColumns}
        dataSource={rentRoomInfos}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row className={styles.tableSummary}>
              {tableInfo.tableSummary}
            </Table.Summary.Row>
          </Table.Summary>
        )}
        footer={() => tableInfo.tableFooter}
      />

      <CouponSelector ref={couponSelectorRef} />
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(memo(Index));
